import { SiteName } from 'lib/constants/brand-theme-site';
import { Data } from '@generated/Foundation.GMI.model';

/**
 * Site-specific Organization Schema definitions
 * Each site can have its own unique organization schema markup
 */
export interface SiteOrganizationSchema {
  '@context': string;
  '@type': string;
  name?: string;
  alternateName?: string | string[];
  url?: string;
  logo?: string;
  description?: string;
  slogan?: string;
  foundingDate?: string;
  legalName?: string;
  founder?: string;
  parentOrganization?: { '@type': string; name?: string };
  contactPoint?: string;
  address?: {
    '@type': string;
    streetAddress?: string;
    addressLocality?: string;
    addressRegion?: string;
    addressCountry?: string;
    postalCode?: string;
  };
  sameAs?: string[];
}

/**
 * Returns null/undefined-safe string value from a Sitecore Field
 */
function fieldVal(field: { value?: string } | undefined): string | undefined {
  const v = field?.value?.trim();
  return v || undefined;
}

/**
 * Splits a multi-line field value into a string array, filtering blank lines.
 */
function fieldLines(field: { value?: string } | undefined): string[] | undefined {
  const v = field?.value?.trim();
  if (!v) return undefined;
  const lines = v
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  return lines.length > 0 ? lines : undefined;
}

/**
 * Builds an organization schema JSON object from Data.Settings.SeoSettings fields.
 * Keys with null or blank values are omitted.
 */
export function buildOrganizationSchemaFromSeoSettings(
  seoSettings: Data.Settings.SeoSettings
): SiteOrganizationSchema | null {
  const seoFields = seoSettings?.fields;
  if (!seoFields) return null;

  const addressFields = {
    ...(fieldVal(seoFields.streetAddress) && { streetAddress: fieldVal(seoFields.streetAddress) }),
    ...(fieldVal(seoFields.addressLocality) && {
      addressLocality: fieldVal(seoFields.addressLocality),
    }),
    ...(fieldVal(seoFields.addressRegion) && { addressRegion: fieldVal(seoFields.addressRegion) }),
    ...(fieldVal(seoFields.addressCountry) && {
      addressCountry: fieldVal(seoFields.addressCountry),
    }),
    ...(fieldVal(seoFields.postalCode) && { postalCode: fieldVal(seoFields.postalCode) }),
  };

  const hasAddress = Object.keys(addressFields).length > 0;

  const alternateNameLines = fieldLines(seoFields.alternateName);
  const sameAsLines = fieldLines(seoFields.sameAs);

  const schema: SiteOrganizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    ...(fieldVal(seoFields.name) && { name: fieldVal(seoFields.name) }),
    ...(alternateNameLines && {
      alternateName: alternateNameLines.length === 1 ? alternateNameLines[0] : alternateNameLines,
    }),
    ...(fieldVal(seoFields.url) && { url: fieldVal(seoFields.url) }),
    ...(fieldVal(seoFields.logo) && { logo: fieldVal(seoFields.logo) }),
    ...(fieldVal(seoFields.description) && { description: fieldVal(seoFields.description) }),
    ...(fieldVal(seoFields.slogan) && { slogan: fieldVal(seoFields.slogan) }),
    ...(fieldVal(seoFields.foundingDate) && { foundingDate: fieldVal(seoFields.foundingDate) }),
    ...(fieldVal(seoFields.legalName) && { legalName: fieldVal(seoFields.legalName) }),
    ...(fieldVal(seoFields.founder) && { founder: fieldVal(seoFields.founder) }),
    ...(fieldVal(seoFields.parentOrganization) && {
      parentOrganization: { '@type': 'Organization', name: fieldVal(seoFields.parentOrganization) },
    }),
    ...(fieldVal(seoFields.contactPoint) && { contactPoint: fieldVal(seoFields.contactPoint) }),
    ...(hasAddress && { address: { '@type': 'PostalAddress', ...addressFields } }),
    ...(sameAsLines && { sameAs: sameAsLines }),
  };

  return schema;
}

/**
 * Get organization schema for a specific site.
 * Reads from seoSettings (Sitecore-managed) if available; returns null otherwise.
 */
export function getSiteOrganizationSchema(
  _siteName: SiteName,
  seoSettings: Data.Settings.SeoSettings | undefined
): SiteOrganizationSchema | null {
  if (seoSettings) {
    return buildOrganizationSchemaFromSeoSettings(seoSettings);
  }

  return null;
}
